<template>
  <View
    ref="scroller"
    class="date-picker"
    :class="{ 'fade-start': canScrollStart, 'fade-end': canScrollEnd }"
    align="center"
    gap="sm"
  >
    <View
      direction="column"
      gap="sm"
      :shrink="0"
      v-for="(monthKeyValue, monthKey, monthIndex) in generateDisplayedDays()"
      :key="monthIndex"
    >
      <Text variant="label-medium" class="month-title">{{ monthKey }}</Text>
      <View gap="sm">
        <div
          class="date-card"
          v-for="(day, dayIndex) in monthKeyValue"
          @click="selectDate(day.date)"
          :key="dayIndex"
          :class="{ selected: day.date.getTime() === model?.getTime() }"
        >
          <Text class="day" variant="body-small">{{ day.dayIndex }}</Text>
          <Text class="date" variant="h5">{{ day.monthDayIndex }}</Text>
        </div>
      </View>
    </View>
  </View>
</template>

<script setup lang="ts">
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { computed, ref, useTemplateRef } from 'vue';

import Text from './text.vue';
import View from './view/view.vue';

const model = defineModel<Date | null>();

const scrollerRef = useTemplateRef('scroller');
const scrollElement = computed<HTMLElement | undefined>(() => scrollerRef.value?.$el);
const canScrollStart = ref(false);
const canScrollEnd = ref(false);
const scrollEdgeThreshold = 1;

function updateScrollAffordance() {
  const element = scrollElement.value;

  if (!element) {
    return;
  }

  const maxScrollLeft = element.scrollWidth - element.clientWidth;

  canScrollStart.value = element.scrollLeft > scrollEdgeThreshold;
  canScrollEnd.value = element.scrollLeft < maxScrollLeft - scrollEdgeThreshold;
}

useEventListener(scrollElement, 'scroll', updateScrollAffordance, { passive: true });
useResizeObserver(scrollElement, updateScrollAffordance);

function generateDisplayedDays() {
  const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const availableDaysGroupedByMonth: {
    [key: string]: {
      dayIndex: string;
      monthDayIndex: number;
      date: Date;
    }[];
  } = {};
  const nextFourWeeksInDays = 28;

  for (let i = 0; i < nextFourWeeksInDays; i++) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + i);

    const monthName = date.toLocaleString('default', { month: 'long' });

    if (!availableDaysGroupedByMonth[monthName]) {
      availableDaysGroupedByMonth[monthName] = [];
    }

    availableDaysGroupedByMonth[monthName].push({
      dayIndex: days[date.getDay()],
      monthDayIndex: date.getDate(),
      date: date,
    });
  }

  return availableDaysGroupedByMonth;
}

function selectDate(date: Date) {
  model.value = date;
}
</script>

<style scoped lang="scss">
.date-picker {
  --fade-size: 1.5rem;
  --fade-start: 0px;
  --fade-end: 0px;

  overflow-x: auto;
  // overflow-x alone turns the vertical axis into a scroll container as well, so any
  // sub-pixel overflow paints a full-width vertical scrollbar next to the day cards.
  overflow-y: hidden;
  padding-bottom: var(--t-spacing-sm);
  // fades whichever edge still has days behind it, so the row never looks like it simply ends
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--fade-start),
    #000 calc(100% - var(--fade-end)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 var(--fade-start),
    #000 calc(100% - var(--fade-end)),
    transparent 100%
  );

  &.fade-start {
    --fade-start: var(--fade-size);
  }

  &.fade-end {
    --fade-end: var(--fade-size);
  }

  // Chromium and Safari: styling ::-webkit-scrollbar opts the scroller out of overlay
  // scrollbars, so the bar stays visible at rest instead of fading after each scroll.
  &::-webkit-scrollbar {
    height: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: var(--t-color-neutral-100);
    border-radius: var(--t-border-radius-xs);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--t-color-neutral-300);
    border-radius: var(--t-border-radius-xs);
  }

  &:hover::-webkit-scrollbar-thumb {
    background: var(--t-color-neutral-400);
  }

  // Firefox has no ::-webkit-scrollbar and needs the standard properties. They must stay
  // gated: Chromium discards the whole block above whenever it sees either one of them set.
  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: var(--t-color-neutral-300) var(--t-color-neutral-100);
  }
}

.date-card {
  border-radius: var(--t-border-radius-sm);
  padding: 0.5rem;
  cursor: pointer;
  width: 2.5rem;
  height: 3.5rem;
  border: 1px solid transparent;
  flex-shrink: 0;

  &.selected {
    background-color: var(--t-color-neutral-950);

    .day,
    .date {
      color: var(--t-color-neutral-white);
    }
  }

  &:hover {
    border: 1px solid var(--t-color-neutral-950);
  }
}
</style>
